import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, Loading } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { ReconcileDetailDrawer } from "@/components/mobilfunk/ReconcileDetailDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtEUR } from "@/lib/format";
import { Scale, ClipboardCheck } from "lucide-react";

const STATUS = {
  uebereinstimmung: { label: "Übereinstimmung", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" },
  unterzahlung: { label: "Unterzahlung", cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" },
  ueberzahlung: { label: "Überzahlung", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20" },
  fehlt: { label: "Abrechnung fehlt", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  pruefung: { label: "Soll unsicher", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  offen: { label: "Offen", cls: "bg-muted text-muted-foreground border-border" },
};

export default function MfReconcile() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canSettle = can(user?.role, "mobilfunk:settle");
  const [taskRow, setTaskRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["mf-reconciliation"], queryFn: async () => (await api.get("/mobilfunk/reconciliation")).data });

  if (isLoading) return <Loading />;
  const rows = data?.rows || [];

  const columns = [
    { key: "customer", header: "Abschluss", render: (r) => (
      <div className="min-w-0"><div className="truncate font-medium">{r.customer_name}</div>
        <div className="text-xs text-muted-foreground">{r.external_order_no || "ohne Auftragsnr."}{r.has_storno && <span className="ml-1.5 text-amber-600">Storno</span>}</div></div>
    ) },
    { key: "soll", header: "Soll", render: (r) => fmtEUR(r.soll) },
    { key: "ist", header: "Ist", render: (r) => fmtEUR(r.ist) },
    { key: "diff", header: "Differenz", render: (r) => <span className={r.difference < 0 ? "text-red-600" : r.difference > 0 ? "text-sky-600" : ""}>{fmtEUR(r.difference)}</span> },
    { key: "status", header: "Status", render: (r) => <Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge> },
    { key: "action", header: "", render: (r) => (
      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        {r.reclamation_task_id ? <Badge variant="secondary" data-testid={`has-task-${r.contract_id}`}>Aufgabe erstellt</Badge>
          : (canSettle && ["unterzahlung", "ueberzahlung", "fehlt"].includes(r.status) &&
            <Button size="sm" variant="outline" onClick={() => setTaskRow(r)} data-testid={`create-task-${r.contract_id}`}>Aufgabe</Button>)}
      </div>
    ) },
  ];

  return (
    <div data-testid="mf-reconcile">
      <PageHeader title="Soll-Ist-Abgleich" description={`Toleranz: ${fmtEUR(data?.tolerance?.amount || 0)} / ${data?.tolerance?.percent || 0}% · ${data?.unmatched?.length || 0} nicht zugeordnet · ${data?.duplicate_count || 0} doppelt`} />
      <DataTable columns={columns} rows={rows} testIdPrefix="recon-row" keyField="contract_id" onRowClick={(r) => setDetail(r.contract_id)} />
      {(data?.unmatched || []).length > 0 && (
        <SectionCard title="Nicht zugeordnete Abrechnungspositionen" className="mt-4">
          <div className="space-y-1.5">{data.unmatched.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="recon-unmatched">
              <span>{u.external_contract_no || "—"} · {u.abrechnungsnummer}</span>
              <span className="flex items-center gap-2">{fmtEUR(u.amount)}<Badge className="bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20">{u.match_status}</Badge></span>
            </div>
          ))}</div>
          <p className="mt-2 text-xs text-muted-foreground">Zuordnung unter „Abrechnungen" → Abrechnung öffnen → Position zuordnen.</p>
        </SectionCard>
      )}
      <DiffTaskDialog row={taskRow} onClose={() => setTaskRow(null)} onDone={() => { setTaskRow(null); qc.invalidateQueries({ queryKey: ["mf-reconciliation"] }); }} />
      <ReconcileDetailDrawer contractId={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function DiffTaskDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ title: "", priority: "hoch", due_date: "" });
  useEffect(() => {
    if (row) setF({ title: `Provisionsdifferenz ${row.customer_name} (${fmtEUR(row.difference)})`, priority: "hoch", due_date: "" });
  }, [row]);
  const submit = async () => {
    try { await api.post("/mobilfunk/reconciliation/task", { contract_id: row.contract_id, title: f.title, priority: f.priority, due_date: f.due_date }); toast.success("Aufgabe erstellt"); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="diff-task-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" />Reklamations-Aufgabe</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="diff-task-title" /></div>
          <div className="space-y-1.5"><Label>Fällig am</Label><Input type="date" value={f.due_date} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={submit} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="diff-task-save">Aufgabe erstellen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
